import type { Edge, Node } from '@xyflow/react'
import {
  UniversalNodeRegistry,
  isAddableSchema,
  type UniversalNodeSchema,
} from '@/features/backtest/schemas/universal-node-schema'
import { connectableCategoriesWhere, type ConnectableCategory } from './connectable-sources'
import { validateNodeConnection } from './universal-node-validation'
import { resolvePassthroughDownstream, type PassthroughConsumer } from './inline-passthroughs'
import { nodeName } from './node-display-name'

/**
 * What a subnet input needs, said from outside the node (Chris, 2026-10-27:
 * a template arrived with no indication of what its inputs need; spec
 * docs/superpowers/specs/2026-10-27-subnet-input-needs-design.md). A pill
 * stores only a name, so the need is derived each time from the real nodes it
 * feeds inside, by the editor's own wire rule (validateNodeConnection): it
 * cannot go stale, and templates get it for free.
 */
export interface SubnetInputNeed {
  /** It feeds a node that is not a debug sink, so unwired it reads as missing. */
  required: boolean
  /** The empty control's prompt ("Connect Price Data"). */
  text: string
  /** The "?" list: the palette's categories, narrowed to sources that feed every consumer. */
  connectable: ConnectableCategory[]
  /** When the consumers disagree, the two that conflict ("Condition (Input A)"). */
  conflict?: [string, string]
}

// Each prompt, and what the pill's hover says the input needs. Price labels
// name the node, never a bare "Price".
const PROMPTS = {
  price: { text: 'Connect Price Data', needs: 'Price Data' },
  priceOrNumber: { text: 'Connect Price Data or a number', needs: 'Price Data or a number' },
  number: { text: 'Connect a number', needs: 'a number' },
  condition: { text: 'Connect a condition', needs: 'a condition' },
  any: { text: 'Connect a source…', needs: 'a source' },
}
const DISAGREE = 'Inside nodes disagree'
const NEEDS = new Map(Object.values(PROMPTS).map((prompt) => [prompt.text, prompt.needs]))

/**
 * The consumers a need is judged by. A debug sink takes anything, so it never
 * narrows a need, and an unknown node has no rule to ask.
 */
export function realConsumers(consumers: PassthroughConsumer[]): PassthroughConsumer[] {
  return consumers.filter((consumer) => {
    const schema = UniversalNodeRegistry.getSchema(consumer.type)
    return !!schema && schema.category !== 'debug'
  })
}

/** A consumer as messages name it: the node, then its socket ("Condition (Input A)"). */
export function consumerName(consumer: PassthroughConsumer): string {
  const schema = UniversalNodeRegistry.getSchema(consumer.type)
  const name = nodeName({ type: consumer.type, data: consumer.data })
  const input = schema?.inputs.find((i) => i.id === consumer.handle)
  // A dynamic input (an And/Or Gate's rows) has no name of its own: the node's says it.
  const socket = input?.faceLabel || input?.displayName
  return socket ? `${name} (${socket})` : name
}

/** A pass-through as messages name it: a pill by its label, a reroute as itself. */
export function passthroughName(node: { type?: string; data?: Record<string, unknown> }): string {
  const label = typeof node.data?.label === 'string' ? node.data.label : ''
  if (label) return label
  if (node.type === 'reroute') return 'This reroute'
  return node.type === 'subnetOutput' ? 'Out' : 'In'
}

/** One output the palette can add, and which of the consumers it feeds. */
interface Feed {
  schema: UniversalNodeSchema
  /** `type:output`, as connectableCategoriesWhere is asked about it. */
  key: string
  type: string
  /** A pass-through output (Bar Offset) carries whatever it is fed, so it says nothing about the kind. */
  typed: boolean
  feeds: boolean[]
}

// The same loop as connectableCategories (connectable-sources.ts): every
// output of every node the palette can add, asked of each consumer once.
function feedsOf(consumers: PassthroughConsumer[]): Feed[] {
  const feeds: Feed[] = []
  for (const schema of UniversalNodeRegistry.getAllSchemas()) {
    if (!isAddableSchema(schema)) continue
    for (const output of schema.outputs) {
      feeds.push({
        schema,
        key: `${schema.type}:${output.id}`,
        type: output.type,
        typed: !output.passthrough,
        feeds: consumers.map(
          (consumer) =>
            validateNodeConnection(
              schema.type,
              consumer.type,
              consumer.handle || undefined,
              output.id,
              undefined,
              consumer.data as Record<string, unknown> | undefined,
            ).isValid,
        ),
      })
    }
  }
  return feeds
}

const feedsAll = (feed: Feed, indices: number[]) => indices.every((i) => feed.feeds[i])

/** The kinds of value (ohlcv, number, boolean...) some real output feeds all these consumers with. */
function kindsFeeding(feeds: Feed[], indices: number[]): Set<string> {
  return new Set(feeds.filter((f) => f.typed && feedsAll(f, indices)).map((f) => f.type))
}

function promptFor(kinds: Set<string>): (typeof PROMPTS)[keyof typeof PROMPTS] {
  if (kinds.has('boolean')) {
    return kinds.has('number') || kinds.has('ohlcv') ? PROMPTS.any : PROMPTS.condition
  }
  if (kinds.has('ohlcv')) return kinds.has('number') ? PROMPTS.priceOrNumber : PROMPTS.price
  if (kinds.has('number')) return PROMPTS.number
  return PROMPTS.any
}

// Grouped by connectableCategoriesWhere, so the "?" reads the same on a
// subnet's row as on any node's input.
function categoriesFeeding(feeds: Feed[], indices: number[]): ConnectableCategory[] {
  const fits = new Set(feeds.filter((f) => feedsAll(f, indices)).map((f) => f.key))
  return connectableCategoriesWhere((schema, output) => fits.has(`${schema.type}:${output.id}`))
}

/** The first two consumers no kind of value feeds both, else the first and the last. */
function conflictOf(feeds: Feed[], consumers: PassthroughConsumer[]): [string, string] {
  for (let i = 0; i < consumers.length; i++) {
    for (let j = i + 1; j < consumers.length; j++) {
      if (kindsFeeding(feeds, [i, j]).size === 0) {
        return [consumerName(consumers[i]), consumerName(consumers[j])]
      }
    }
  }
  return [consumerName(consumers[0]), consumerName(consumers[consumers.length - 1])]
}

/** What a set of consumers needs from the one value they will all read. */
function needOf(all: PassthroughConsumer[]): SubnetInputNeed {
  const consumers = realConsumers(all)
  const feeds = feedsOf(consumers)
  const every = consumers.map((_, i) => i)
  const kinds = kindsFeeding(feeds, every)
  const required = consumers.length > 0
  if (kinds.size === 0 && consumers.length > 1) {
    return { required, text: DISAGREE, connectable: [], conflict: conflictOf(feeds, consumers) }
  }
  return {
    required,
    text: promptFor(kinds).text,
    // Nothing typed fits: Bar Offset alone would only pass the disagreement on.
    connectable: kinds.size === 0 ? [] : categoriesFeeding(feeds, every),
  }
}

/** What the subnet input pill `pillId` needs, from the real nodes it feeds. */
export function subnetInputNeed(pillId: string, nodes: Node[], edges: Edge[]): SubnetInputNeed {
  return needOf(resolvePassthroughDownstream(pillId, nodes, edges))
}

/** The pill's hover while it needs wiring: what it needs and where it is wired from. */
export function subnetInputHint(need: SubnetInputNeed): string {
  if (need.conflict) {
    return `${DISAGREE}: ${need.conflict[0]} and ${need.conflict[1]} take different things.`
  }
  return `Needs ${NEEDS.get(need.text) ?? PROMPTS.any.needs}. Wire it from outside the subnet.`
}

/**
 * The wire check for a pass-through nothing feeds yet (canConnect): a wire out
 * of it adds `joining` (the input wired, or every real node a pass-through
 * wired after it feeds) to what it already feeds, and some kind of value must
 * still feed them all. Returns the consumer the joining ones share no need
 * with, else null. Consumers that already disagree (a saved graph) are left to
 * say so on the card: the rule governs new wires, and refusing this one would
 * not mend them.
 */
export function conflictingConsumer(
  others: PassthroughConsumer[],
  joining: PassthroughConsumer[],
): PassthroughConsumer | null {
  const next = realConsumers(joining)
  const judged = realConsumers(others)
  if (next.length === 0 || judged.length === 0) return null
  const feeds = feedsOf([...judged, ...next])
  const before = judged.map((_, i) => i)
  const after = next.map((_, i) => judged.length + i)
  if (kindsFeeding(feeds, before).size === 0) return null
  if (kindsFeeding(feeds, [...before, ...after]).size > 0) return null
  return judged.find((_, i) => kindsFeeding(feeds, [i, ...after]).size === 0) ?? judged[0]
}
